// src/components/ExperimentCard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    Card,
    Typography,
    Stack,
    Divider,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    useTheme,
    useMediaQuery,
    FormControlLabel,
    Checkbox,
    Chip,
    Slider,
    Collapse,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    Tooltip,
    Alert,
    LinearProgress,
    Skeleton,
    Avatar,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { alpha } from '@mui/material/styles';

import { getLabResultByExperimentId } from '../infra/api/labResultApi';
import {
    formatarValorLabExibicao,
    UNIDADE_LEGADO,
} from '../utils/numeroLab';
import { SnackbarAlert } from './SnackbarAlert';
import ResultadoLabDialog from './ResultadoLabDialog';
import {
    agruparEntradasLab,
    entradasLabDoProtocolo,
    normalizarResultadoLab,
} from '../utils/resultadoLab';
import {
    addComentarioExperimento,
    encerrarExperimento,
    encerrarExperimentoForcadoAdmin,
    getUltimaLeituraExperimento,
    excluirExperimento,
    restaurarExperimento,
    atualizarDiluicaoExperimento,
    atualizarAmostraExperimento,
    atualizarTrainingLabelExperimento,
} from '../infra/api/ExperimentoApi';

import Permitido from '../security/Permitido';
import { usePermissao } from '../security/usePermissao';
import { usePerfilPlano } from '../security/usePerfilPlano';
import { diluicaoSeAplica, isSoPresenca, secoesDoResultado } from '../utils/predicao';

const DEFAULT_TAG_COLOR = '#1976d2';
const DEFAULT_TRAINING_LABEL = 'APTO_TREINAMENTO';

const TRAINING_LABEL_META = {
    APTO_TREINAMENTO: { label: 'Apto treino', color: 'success' },
    REVISAR: { label: 'Revisar', color: 'warning' },
    DESCARTAR: { label: 'Descartar', color: 'error' },
};

const parseLabValue = (value) => {
    if (value === null || value === undefined || value === '—') return NaN;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        if (value.toLowerCase().includes('e')) {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? NaN : parsed;
        }
        const cleanStr = value.replace(/\./g, '').replace(',', '.');
        const parsed = parseFloat(cleanStr);
        return isNaN(parsed) ? NaN : parsed;
    }
    return NaN;
};

const toScientificResult = (value) => {
    const num = parseLabValue(value);
    if (isNaN(num)) return '—';
    if (num === 0) return '0';
    const THRESHOLD_DIGITS = 1000000;
    if (Math.abs(num) >= 0.01 && Math.abs(num) < THRESHOLD_DIGITS) {
        return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(num);
    }
    return new Intl.NumberFormat('pt-BR', { notation: 'scientific', maximumFractionDigits: 0 })
        .format(num)
        .toUpperCase();
};

const safeFormatRaw = (value) => {
    const num = parseLabValue(value);
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 }).format(num);
};

const toLogSubValue = (value) => {
    const num = parseLabValue(value);
    if (isNaN(num) || num <= 0) return null;
    const logValue = Math.log10(num).toFixed(2);
    return `log: ${logValue.replace('.', ',')}`;
};

const formatSecondsAgo = (seconds) => {
    if (seconds == null) return '—';
    if (seconds < 60) return `${seconds} segundos`;
    return `${Math.round(seconds / 60)} minutos`;
};

const normalizeId = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        const m = val.match(/ObjectId\("([^"]+)"\)/);
        if (m) return m[1];
        try {
            const parsed = JSON.parse(val);
            if (parsed?.$oid) return String(parsed.$oid);
        } catch {}
        return val;
    }
    if (typeof val === 'object' && val?.$oid) return String(val.$oid);
    return String(val);
};

const useProtoMap = (protocols) =>
    React.useMemo(() => {
        const map = new Map();
        (protocols || []).forEach((p) => {
            const ids = [p?.id, p?._id];
            ids.forEach((i) => {
                if (i) map.set(normalizeId(i), p);
            });
        });
        return map;
    }, [protocols]);

function ExperimentCard({
    experiment,
    onAction,
    canNavigate,
    isViewed,
    isLastViewed,
    towerTag,
    towerTagColor,
    towerTagLoading = false,
    allTowers = [],
    protocols = [],
    selectable = false,
    selected = false,
    onToggleSelect,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { pode } = usePermissao();
    const { resultadoQualitativo } = usePerfilPlano();

    const [modalType, setModalType] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [labResult, setLabResult] = useState(null);
    // Chaveado por `${bacteriaId}|${unit}`, nao por indice: com a mesma bacteria em
    // duas unidades, indice nao distingue as entradas.
    const [sampleData, setSampleData] = useState(experiment.amostraData || {});
    const [sampleInputs, setSampleInputs] = useState({ nome: '', codigoAmostra: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success', action: null });
    const [comments, setComments] = useState(experiment.comments || []);
    const [dilN, setDilN] = useState(experiment.diluicao ?? 0);
    const [hasUserEdited, setHasUserEdited] = useState(false);
    const [savingDil, setSavingDil] = useState(false);
    const [sliderValue, setSliderValue] = useState(dilN);
    const [isEditingDilution, setIsEditingDilution] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);
    const [confirmEncerrarOpen, setConfirmEncerrarOpen] = useState(false);
    const [encerrarOpen, setEncerrarOpen] = useState(false);
    const [encerrarForcado, setEncerrarForcado] = useState(false);
    const [encerrarMotivo, setEncerrarMotivo] = useState('');
    const [encerrarRemoveTipo, setEncerrarRemoveTipo] = useState(false);
    const [ending, setEnding] = useState(false);
    const [adminForcarOpen, setAdminForcarOpen] = useState(false);
    const [adminForcarMotivo, setAdminForcarMotivo] = useState('');
    const [forcandoAdmin, setForcandoAdmin] = useState(false);
    const [ultimaLeituraInfo, setUltimaLeituraInfo] = useState(null);
    const [deleteMotivo, setDeleteMotivo] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [trainingLabel, setTrainingLabel] = useState(experiment.trainingLabel || DEFAULT_TRAINING_LABEL);
    const [trainingLabelInput, setTrainingLabelInput] = useState(experiment.trainingLabel || DEFAULT_TRAINING_LABEL);
    const [trainingLabelReason, setTrainingLabelReason] = useState('');
    const [savingTrainingLabel, setSavingTrainingLabel] = useState(false);

    const protoMap = useProtoMap(protocols);
    const protoIdRaw =
        experiment?.protocolo?.idProtocolo ||
        experiment?.protocolo?.idProtocol ||
        experiment?.protocolId ||
        experiment?.protocol?._id ||
        experiment?.protocol?.id;

    // Campos do resultado de laboratório: um por (bactéria, unidade) do protocolo.
    const entradasLab = React.useMemo(
        () => entradasLabDoProtocolo(protoMap.get(normalizeId(protoIdRaw))),
        [protoMap, protoIdRaw]
    );
    const gruposLab = React.useMemo(() => agruparEntradasLab(entradasLab), [entradasLab]);

    const analysisIdRaw =
        experiment?.protocolo?.analiseId ||
        experiment?.protocol?.analysisId ||
        experiment?.metadata?.lastAnalysis?.analiseId ||
        experiment?.ultimaAnalise?.analiseId;

    const getModalPaperProps = (maxWidth = 'sm') => ({
        fullWidth: true,
        maxWidth: maxWidth,
        scroll: 'paper',
        PaperProps: {
            sx: {
                borderRadius: isMobile ? 2 : 3,
                m: isMobile ? 1 : 4,
                width: isMobile ? 'calc(100% - 16px)' : undefined,
                maxHeight: isMobile ? 'calc(100% - 32px)' : undefined,
            },
        },
    });

    useEffect(() => {
        if (experiment.id)
            getLabResultByExperimentId(experiment.id)
                .then(r => setLabResult(normalizarResultadoLab(r)))
                .catch(() => setLabResult(null));
    }, [experiment.id]);

    useEffect(() => { setComments(experiment.comments || []); }, [experiment.comments]);
    useEffect(() => { setSampleData(experiment.amostraData || {}); }, [experiment.amostraData]);
    useEffect(() => {
        const next = experiment.trainingLabel || DEFAULT_TRAINING_LABEL;
        setTrainingLabel(next);
        setTrainingLabelInput(next);
    }, [experiment.trainingLabel]);

    useEffect(() => {
        const valorAtual = experiment.diluicao ?? 0;
        if (!isEditingDilution && !hasUserEdited) {
            setDilN(valorAtual);
            setSliderValue(valorAtual);
        }
    }, [experiment.diluicao, isEditingDilution, hasUserEdited]);

    const handleMenuClick = (event) => { event.stopPropagation(); setAnchorEl(event.currentTarget); };
    const handleMenuClose = () => { setAnchorEl(null); };
    const handleMenuAction = (action) => { handleMenuClose(); if (action) action(); };

    const checkGuestPermission = () => {
        if (!pode('dados.alterar')) {
            setSnackbar({ open: true, message: 'Modo Visitante: Ação de edição bloqueada.', severity: 'info' });
            return false;
        }
        return true;
    };

    const handleDilutionChange = async (n) => {
        if (n === dilN) { setIsEditingDilution(false); return; }
        if (!checkGuestPermission()) { setSliderValue(dilN); return; }
        const prev = dilN;
        setDilN(n);
        setSavingDil(true);
        try {
            await atualizarDiluicaoExperimento(experiment.id, n);
            setHasUserEdited(true);
            experiment.diluicao = n;
            setSnackbar({ open: true, message: `Diluição: 10⁻${n}`, severity: 'success' });
            onAction?.({ type: 'refresh' });
        } catch (error) {
            setDilN(prev);
            setSliderValue(prev);
            setSnackbar({ open: true, message: 'Erro ao atualizar', severity: 'error' });
        } finally {
            setSavingDil(false);
            setIsEditingDilution(false);
        }
    };

    const openModal = (type) => {
        if (type === 'sample') setSampleInputs({ nome: sampleData.nome || '', codigoAmostra: sampleData.codigoAmostra || '' });
        if (type === 'trainingLabel') { setTrainingLabelInput(trainingLabel || DEFAULT_TRAINING_LABEL); setTrainingLabelReason(''); }
        if (type === 'confirm') setDeleteMotivo('');
        setModalType(type);
    };
    const closeModal = () => setModalType(null);

    const saveTrainingLabel = async () => {
        if (!checkGuestPermission()) return;
        if (!pode('amostra.rotuloTreino')) { setSnackbar({ open: true, message: 'Somente ADMIN pode alterar.', severity: 'warning' }); return; }
        if (savingTrainingLabel) return;
        setSavingTrainingLabel(true);
        try {
            const updated = await atualizarTrainingLabelExperimento(experiment.id, trainingLabelInput, trainingLabelReason);
            const next = updated?.trainingLabel || trainingLabelInput;
            setTrainingLabel(next);
            experiment.trainingLabel = next;
            closeModal();
            setSnackbar({ open: true, message: 'Label atualizado.', severity: 'success' });
            onAction?.({ type: 'refresh' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Falha ao atualizar o label.', severity: 'error' });
        } finally {
            setSavingTrainingLabel(false);
        }
    };

    const saveSampleData = async () => {
        if (!checkGuestPermission()) return;
        try {
            const updated = await atualizarAmostraExperimento(experiment.id, sampleInputs.nome, sampleInputs.codigoAmostra);
            setSampleData(updated.amostraData || {});
            closeModal();
            setSnackbar({ open: true, message: 'Dados salvos!', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Erro ao salvar.', severity: 'error' });
        }
    };

    const saveComment = async () => {
        if (!checkGuestPermission()) return;
        try {
            const updated = await addComentarioExperimento(experiment.id, inputValue);
            setComments(updated.comments || []);
            setInputValue('');
            closeModal();
            setSnackbar({ open: true, message: 'Comentário adicionado!', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Erro.', severity: 'error' });
        }
    };

    const handleEncerrarConfirm = async () => {
        if (!checkGuestPermission()) return;
        if (ending) return;
        setEnding(true);
        try {
            const updated = await encerrarExperimento(experiment.id, encerrarForcado, encerrarMotivo || undefined);
            if (updated?.generalInfo?.status)
                experiment.generalInfo = { ...experiment.generalInfo, status: updated.generalInfo.status };
            setSnackbar({ open: true, message: 'Encerrado!', severity: 'success' });
            setEncerrarOpen(false);
            if (encerrarRemoveTipo) onAction?.({ type: 'remove-assay-type', experimentId: experiment.id });
            onAction?.({ type: 'refresh' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Erro ao encerrar.', severity: 'error' });
        } finally {
            setEnding(false);
        }
    };

    const openAdminForcarDialog = () => {
        setUltimaLeituraInfo(null);
        setAdminForcarOpen(true);
        getUltimaLeituraExperimento(experiment.id)
            .then((info) => setUltimaLeituraInfo(info))
            .catch(() => setUltimaLeituraInfo(null));
    };

    const handleAdminForcarConfirm = async () => {
        if (!pode('amostra.forcarEncerramento')) {
            setSnackbar({ open: true, message: 'Somente ADMIN pode forçar encerramento.', severity: 'warning' });
            return;
        }
        if (forcandoAdmin) return;
        setForcandoAdmin(true);
        try {
            const updated = await encerrarExperimentoForcadoAdmin(experiment.id, adminForcarMotivo);
            if (updated?.status)
                experiment.generalInfo = { ...experiment.generalInfo, status: updated.status };
            setSnackbar({ open: true, message: 'Experimento encerrado forçadamente.', severity: 'success' });
            setAdminForcarOpen(false);
            setAdminForcarMotivo('');
            onAction?.({ type: 'refresh' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Erro ao forçar encerramento.', severity: 'error' });
        } finally {
            setForcandoAdmin(false);
        }
    };

    const [restaurando, setRestaurando] = useState(false);
    const handleRestaurar = async () => {
        if (!checkGuestPermission() || restaurando) return;
        setRestaurando(true);
        try {
            await restaurarExperimento(experiment.id);
            setSnackbar({ open: true, message: 'Experimento restaurado.', severity: 'success' });
            onAction?.({ type: 'refresh' });
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.message || 'Erro ao restaurar.', severity: 'error' });
        } finally {
            setRestaurando(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!checkGuestPermission()) return;
        if (deleting) return;
        setDeleting(true);
        try {
            await excluirExperimento(experiment.id, deleteMotivo || undefined);
            setSnackbar({ open: true, message: 'Excluído.', severity: 'success' });
            setModalType(null);
            onAction?.({ type: 'refresh' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Erro ao excluir.', severity: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const { generalInfo, protocolName, analysisName, deviceName, serialNumber, protocolColor } = experiment;
    const gi = generalInfo || {};
    const isActive = (gi.status || '').toLowerCase() === 'active';
    // Excluído (soft delete): só aparece na lixeira; o card vira só leitura + Restaurar.
    const excluido = experiment.ativo === false;
    const startMs = gi.startDate ? Number(gi.startDate) * 1000 : null;
    const endMs = gi.endDate ? Number(gi.endDate) * 1000 : null;

    let progress = 0;
    if (startMs && isActive) {
        const estEndMs = dayjs(startMs).add(24, 'hour').valueOf();
        const elapsed = Date.now() - startMs;
        progress = Math.min(1, Math.max(0, elapsed / (estEndMs - startMs)));
    } else if (startMs && !isActive) progress = 1;

    const { predicao } = experiment;
    const sortedComments = React.useMemo(
        () => [...comments].sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()),
        [comments]
    );
    const latestComment = sortedComments[0];

    const displaySerial = serialNumber || deviceName || '—';
    const displayTag = towerTag;
    const mainTitle = displayTag ? displayTag : displaySerial;
    const mainTitleColor = displayTag ? towerTagColor || DEFAULT_TAG_COLOR : 'text.primary';
    const isMainTitleSerial = !displayTag;
    const secondLineText = displayTag ? displaySerial : protocolName || 'Sem Protocolo';
    const protocolLine = displayTag ? protocolName : null;
    const analysisLine = analysisName || null;
    const effectiveTrainingLabel = trainingLabel || DEFAULT_TRAINING_LABEL;
    const trainingLabelMeta = TRAINING_LABEL_META[effectiveTrainingLabel] || { label: effectiveTrainingLabel, color: 'default' };

    // UMA linha por bacteria no card — nao por unidade (o detalhamento por unidade fica
    // no modal). A predicao de cada linha vem de `secoesResultado`, casada pelo nome.
    const bacteriaList = React.useMemo(
        () => gruposLab.map((g) => ({
            bacteriaId: g.bacteriaId,
            bacteriaNome: g.bacteriaNome,
            entradas: g.entradas,
        })),
        [gruposLab]
    );

    const semDiluicao = !diluicaoSeAplica(predicao);
    const secoesResultado = React.useMemo(
        () => secoesDoResultado({ predicao, bacterias: bacteriaList, labResult, diluicao: dilN }),
        [predicao, bacteriaList, labResult, dilN]
    );

    return (
        <>
            <SnackbarAlert
                open={snackbar.open}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                severity={snackbar.severity}
                message={snackbar.message}
            />

            <Card
                elevation={0}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    borderTop: `3px solid ${protocolColor || theme.palette.primary.main}`,
                    overflow: 'hidden',
                    transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
                    boxShadow: '0 1px 3px rgba(14, 34, 59, 0.07)',
                    '&:hover': {
                        boxShadow: '0 6px 20px rgba(14, 34, 59, 0.12)',
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                    },
                    bgcolor: 'background.paper',
                    ...(selected && {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`,
                    }),
                }}
            >
                {/* ── HEADER ── */}
                <Box sx={{ px: 2, pt: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>

                    {/* Status + badges + menu */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            {selectable && (
                                <Checkbox
                                    size="small"
                                    checked={selected}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        onToggleSelect?.(experiment);
                                    }}
                                    sx={{ p: 0.25, ml: -0.5 }}
                                    inputProps={{ 'aria-label': 'Selecionar experimento' }}
                                />
                            )}
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, bgcolor: isActive ? 'success.main' : 'text.disabled' }} />
                            <Typography variant="caption" fontWeight={700} color={excluido ? 'error.main' : isActive ? 'success.main' : 'text.secondary'} letterSpacing={0.5}>
                                {excluido ? 'EXCLUÍDO' : isActive ? 'ATIVO' : 'ENCERRADO'}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            {isLastViewed && <Lucide.Star size={13} color={theme.palette.warning.main} />}
                            {!isLastViewed && isViewed && <Lucide.Eye size={13} color={theme.palette.text.secondary} />}
                            {!excluido && (
                                <IconButton size="small" onClick={handleMenuClick} sx={{ ml: 0.25 }}>
                                    <Lucide.MoreVertical size={18} />
                                </IconButton>
                            )}
                        </Stack>
                    </Stack>

                    {/* Progress bar */}
                    <LinearProgress
                        variant="determinate"
                        value={progress * 100}
                        sx={{
                            height: 3,
                            borderRadius: 2,
                            bgcolor: alpha(protocolColor || theme.palette.primary.main, 0.12),
                            '& .MuiLinearProgress-bar': {
                                bgcolor: protocolColor || 'primary.main',
                                transition: 'transform 0.8s ease',
                            },
                        }}
                    />

                    {/* Identidade — tudo pode quebrar linha */}
                    {towerTagLoading ? (
                        <Skeleton variant="text" width="55%" height={20} />
                    ) : (
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color={mainTitleColor}
                            sx={{
                                fontFamily: isMainTitleSerial ? 'monospace' : 'inherit',
                                letterSpacing: isMainTitleSerial ? -0.3 : 'inherit',
                                wordBreak: 'break-all',
                                lineHeight: 1.35,
                            }}
                        >
                            {mainTitle}
                        </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, wordBreak: 'break-word', mt: -0.5 }}>
                        {secondLineText}
                    </Typography>

                    {protocolLine && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, wordBreak: 'break-word', mt: -0.5 }}>
                            {protocolLine}
                        </Typography>
                    )}

                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', lineHeight: 1.4, wordBreak: 'break-word', mt: -0.5 }}>
                        {analysisLine || (analysisIdRaw ? `Amostra: ${normalizeId(analysisIdRaw)}` : 'Sem amostra')}
                    </Typography>

                    {/* Timestamps */}
                    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
                        <Lucide.Clock size={12} color={isActive ? theme.palette.success.main : theme.palette.text.disabled} />
                        <Typography variant="caption" color={isActive ? 'success.main' : 'text.disabled'} fontWeight={500}>
                            {startMs ? dayjs(startMs).format('DD/MM HH:mm') : '—'}
                        </Typography>
                        {endMs && (
                            <>
                                <Typography variant="caption" color="text.disabled" sx={{ mx: 0.2 }}>-</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    {dayjs(endMs).format('DD/MM HH:mm')}
                                </Typography>
                            </>
                        )}
                    </Stack>
                </Box>

                {/* ── RESULTADOS POR BACTÉRIA ── */}
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    <Permitido
                        cap="amostra.resultados"
                        fallback={
                            <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1, opacity: 0.55 }}>
                                <Lucide.Lock size={15} color={theme.palette.text.disabled} />
                                <Typography variant="caption" color="text.disabled" fontWeight={600} sx={{ fontSize: '0.72rem' }}>
                                    Dados laboratoriais bloqueados
                                </Typography>
                            </Box>
                        }
                    >
                        {secoesResultado.map((secao, i) => {
                            // Linhas do leitor unico (utils/predicao secoesDoResultado): cada
                            // bacteria casada com o alvo do modelo pelo NOME, na mesma unidade
                            // do laboratorio — o mesmo numero do dialogo do Processar e da
                            // pagina de coleta. `semBacteria` = alvo do modelo que nenhuma
                            // bacteria do protocolo cobre (ainda assim aparece).
                            const b = bacteriaList[i] || { bacteriaId: null, bacteriaNome: secao.titulo, semBacteria: true };
                            const labNmp = secao.labValor;
                            const labUnit = secao.labUnidade || UNIDADE_LEGADO;
                            let iaMain = null;
                            let iaSub = null;

                            if (predicao && secao.alvo) {
                                const presenca = secao.presenca;

                                if (isSoPresenca(predicao) || resultadoQualitativo) {
                                    if (presenca !== undefined) {
                                        iaMain = presenca ? 'Presente' : 'Ausente';
                                    }
                                } else if (presenca === false) {
                                    iaMain = 'Ausente';
                                } else {
                                    const valorIa = secao.predLog === null ? null : 10 ** secao.predLog;
                                    iaMain = toScientificResult(valorIa);
                                    iaSub = toLogSubValue(valorIa);
                                }
                            }

                            return (
                                <Box
                                    key={secao.chave || i}
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: i < secoesResultado.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                    }}
                                >
                                    {/* Nome da bactéria */}
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="text.primary"
                                        display="block"
                                        sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem', wordBreak: 'break-word' }}
                                    >
                                        {b.bacteriaNome || `Bactéria ${i + 1}`}
                                    </Typography>

                                    {/* LAB | IA — lado a lado, flex igual */}
                                    <Stack direction="row" spacing={0} alignItems="flex-start">
                                        <Box sx={{ flex: 1, pr: 1.5 }}>
                                            {/* A unidade fica no cabecalho da coluna: o
                                                valor nao diz nada sem ela, e no protocolo
                                                de Piscicultura e UFC/g, nao NMP. */}
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', letterSpacing: 0.4, mb: 0.4, wordBreak: 'break-word' }}>
                                                LAB{labNmp != null ? ` · ${labUnit}` : ''}
                                            </Typography>
                                            {labNmp != null ? (
                                                <>
                                                    <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ lineHeight: 1.2 }}>
                                                        {formatarValorLabExibicao(labNmp)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>
                                                        {toLogSubValue(labNmp)}
                                                    </Typography>
                                                </>
                                            ) : b.semBacteria ? (
                                                <Typography variant="body2" color="text.disabled">—</Typography>
                                            ) : (
                                                // Era `variant="text"` com `color: text.disabled`:
                                                // no tema light virava cinza sobre branco e o
                                                // laboratorista nao achava onde lancar a bancada.
                                                // E uma acao pendente, entao pede peso de botao.
                                                <Button
                                                    size="small" variant="contained" color="primary" disableElevation
                                                    startIcon={<Lucide.Plus size={13} />}
                                                    sx={{
                                                        minWidth: 0, px: 1, py: 0.25,
                                                        fontSize: '0.7rem', fontWeight: 700,
                                                        textTransform: 'none', lineHeight: 1.4,
                                                        borderRadius: 1.5, boxShadow: 'none',
                                                        '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
                                                    }}
                                                    onClick={() => openModal('labResult')}
                                                >
                                                    Inserir
                                                </Button>
                                            )}
                                        </Box>

                                        <Divider orientation="vertical" flexItem />

                                        <Box sx={{ flex: 1, pl: 1.5 }}>
                                            <Typography variant="caption" color="primary.main" display="block" sx={{ fontSize: '0.6rem', letterSpacing: 0.4, mb: 0.4 }}>
                                                IA PREDICT{iaMain && secao.unidade && !isSoPresenca(predicao) && !resultadoQualitativo && secao.presenca !== false ? ` · ${secao.unidade}` : ''}
                                            </Typography>
                                            {iaMain ? (
                                                <>
                                                    <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ lineHeight: 1.2 }}>
                                                        {iaMain}
                                                    </Typography>
                                                    {iaSub && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                                                            {iaSub}
                                                        </Typography>
                                                    )}
                                                </>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                                                    Pendente
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Permitido>
                </Box>

                {/* ── FOOTER ── */}
                <Box sx={{ px: 2, pt: 1.5, pb: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                    {/* Chips */}
                    <Stack direction="row" spacing={1} flexWrap="nowrap" alignItems="center" sx={{ overflowX: 'auto', '::-webkit-scrollbar': { display: 'none' } }}>
                        <Permitido cap="amostra.diluicao">
                            {semDiluicao ? (
                                // Leite/colostro: a analise vai pura ao tubo e o resultado
                                // ignora a diluicao do experimento — editar nao mudaria nada.
                                <Tooltip title="Esta análise não usa diluição: a contagem ignora a diluição do experimento.">
                                    <Chip
                                        label="Sem diluição"
                                        size="small" variant="outlined"
                                        sx={{ borderRadius: 1.5, height: 24, fontSize: '0.75rem', flexShrink: 0 }}
                                    />
                                </Tooltip>
                            ) : (
                                <Chip
                                    icon={<Lucide.Pencil size={12} />}
                                    label={savingDil ? '...' : `10⁻${dilN}`}
                                    size="small" variant="outlined" color="primary"
                                    onClick={() => setIsEditingDilution(!isEditingDilution)}
                                    sx={{ borderRadius: 1.5, height: 24, fontSize: '0.75rem', flexShrink: 0 }}
                                />
                            )}
                        </Permitido>
                        <Permitido cap="amostra.rotuloTreino">
                            <Chip
                                label={`Treino: ${trainingLabelMeta.label}`}
                                size="small" color={trainingLabelMeta.color} variant="outlined"
                                onClick={() => openModal('trainingLabel')}
                                sx={{ borderRadius: 1.5, height: 24, fontSize: '0.75rem', flexShrink: 0 }}
                            />
                        </Permitido>
                        {sampleData.codigoAmostra && (
                            <Chip label={`Cód: ${sampleData.codigoAmostra}`} size="small"
                                sx={{ borderRadius: 1.5, height: 24, fontSize: '0.75rem', bgcolor: alpha(theme.palette.grey[300], 0.4), flexShrink: 0 }}
                            />
                        )}
                        {experiment.matriz && (
                            <Chip label={experiment.matriz} size="small"
                                sx={{ borderRadius: 1.5, height: 24, fontSize: '0.75rem', bgcolor: alpha(theme.palette.grey[300], 0.4), flexShrink: 0 }}
                            />
                        )}
                    </Stack>

                    {/* Dilution slider */}
                    <Permitido cap="amostra.diluicao">
                        <Collapse in={isEditingDilution && !semDiluicao}>
                            <Box sx={{ px: 2, pt: 2, pb: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                                <Typography variant="caption" color="primary" fontWeight={600}>Ajustar Diluição</Typography>
                                <Slider size="small" value={sliderValue} onChange={(_, v) => setSliderValue(v)} onChangeCommitted={(_, v) => handleDilutionChange(v)} min={0} max={6} step={1} marks valueLabelDisplay="auto" />
                            </Box>
                        </Collapse>
                    </Permitido>

                    {/* Último comentário */}
                    {latestComment && (
                        <Tooltip title="Clique para ver o histórico">
                            <Box
                                onClick={() => openModal('history')}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    bgcolor: theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.common.white, 0.06)
                                        : alpha(theme.palette.grey[200], 0.4),
                                    p: 1, borderRadius: 2, cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.common.white, 0.10)
                                            : alpha(theme.palette.grey[300], 0.6),
                                    },
                                }}
                            >
                                <Lucide.MessageSquare size={14} color={theme.palette.text.secondary} style={{ flexShrink: 0 }} />
                                <Typography
                                    variant="caption"
                                    sx={{ fontStyle: 'italic', lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flexGrow: 1, color: theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary' }}
                                >
                                    "{latestComment.text}"
                                </Typography>
                                {sortedComments.length > 1 && (
                                    <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', height: 18, minWidth: 22, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 'bold', px: 0.5, flexShrink: 0 }}>
                                        +{sortedComments.length - 1}
                                    </Box>
                                )}
                            </Box>
                        </Tooltip>
                    )}

                    {/* Botões de ação */}
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        {excluido ? (
                            <Permitido
                                cap="lixeira.restaurar"
                                fallback={<Typography variant="body2" color="text.secondary" sx={{ flex: 1, py: 1 }}>Experimento excluído</Typography>}
                            >
                                <Button
                                    variant="outlined" fullWidth size="medium"
                                    startIcon={<Lucide.RotateCcw />}
                                    disabled={restaurando}
                                    onClick={(e) => { e.stopPropagation(); handleRestaurar(); }}
                                    sx={{ borderRadius: 2 }}
                                >
                                    {restaurando ? 'Restaurando...' : 'Restaurar'}
                                </Button>
                            </Permitido>
                        ) : isActive ? (
                            <Button
                                variant="contained" color="warning" fullWidth size="medium"
                                startIcon={<Lucide.CircleStop />}
                                onClick={(e) => { e.stopPropagation(); setConfirmEncerrarOpen(true); }}
                                sx={{ boxShadow: 'none', borderRadius: 2 }}
                            >
                                Encerrar
                            </Button>
                        ) : (
                            <Permitido
                                cap="amostra.processarIA"
                                fallback={
                                    <Tooltip title="O processamento de IA é um recurso exclusivo. Faça upgrade de plano.">
                                        <Box sx={{ flex: 1 }}>
                                            <Button variant="contained" color="inherit" disabled fullWidth size="medium" startIcon={<Lucide.Lock />} sx={{ boxShadow: 'none', borderRadius: 2, height: '100%' }}>
                                                Processar
                                            </Button>
                                        </Box>
                                    </Tooltip>
                                }
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Button
                                        variant="contained" color="primary" fullWidth size="medium"
                                        startIcon={<Lucide.FlaskConical />}
                                        onClick={(e) => { e.stopPropagation(); onAction?.({ type: 'process', experiment: { ...experiment, diluicao: dilN } }); }}
                                        sx={{ boxShadow: 'none', borderRadius: 2, height: '100%' }}
                                    >
                                        Processar
                                    </Button>
                                </Box>
                            </Permitido>
                        )}

                        {canNavigate && (
                            <Tooltip title="Ver Gráficos">
                                <Button
                                    variant="contained" color="primary"
                                    sx={{ minWidth: 48, px: 0, borderRadius: 2, boxShadow: 'none' }}
                                    onClick={(e) => { e.stopPropagation(); onAction?.({ type: 'view', experiment }); }}
                                >
                                    <Lucide.ChartNoAxesCombined />
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Card>

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{ elevation: 2, sx: { borderRadius: 2, minWidth: 180 } }}
            >
                <Permitido cap="amostra.resultadoLab">
                    <MenuItem onClick={() => handleMenuAction(() => openModal('labResult'))} dense>
                        <ListItemIcon><Lucide.Microscope size={18} /></ListItemIcon>
                        {labResult ? 'Editar Lab' : 'Adicionar Lab'}
                    </MenuItem>
                </Permitido>

                <Permitido cap="amostra.dadosAmostra">
                    <MenuItem onClick={() => handleMenuAction(() => openModal('sample'))} dense>
                        <ListItemIcon><Lucide.Droplet size={18} /></ListItemIcon>
                        Dados da Amostra
                    </MenuItem>
                </Permitido>

                <MenuItem onClick={() => handleMenuAction(() => openModal('comment'))} dense>
                    <ListItemIcon><Lucide.MessageSquare size={18} /></ListItemIcon>
                    Comentar
                </MenuItem>
                {sortedComments.length > 0 && (
                    <MenuItem onClick={() => handleMenuAction(() => openModal('history'))} dense>
                        <ListItemIcon><Lucide.History size={18} /></ListItemIcon>
                        Ver Histórico
                    </MenuItem>
                )}

                <Permitido cap="amostra.rotuloTreino">
                    <MenuItem onClick={() => handleMenuAction(() => openModal('trainingLabel'))} dense>
                        <ListItemIcon><Lucide.FlaskConical size={18} /></ListItemIcon>
                        Label de Treinamento
                    </MenuItem>
                </Permitido>

                <Divider />

                {pode('amostra.forcarEncerramento') && isActive && (
                    <MenuItem onClick={() => handleMenuAction(openAdminForcarDialog)} dense sx={{ color: 'error.main' }}>
                        <ListItemIcon><Lucide.ShieldAlert size={18} color={theme.palette.error.main} /></ListItemIcon>
                        Forçar Encerramento (Admin)
                    </MenuItem>
                )}

                <Permitido cap="amostra.excluir">
                    {/* Excluir só quando ENCERRADA. Em andamento (ATIVO), excluir apenas
                        esconderia a amostra do CRM enquanto ela continua rodando no device —
                        encerre (ou force o encerramento) antes. */}
                    <Tooltip
                        title={isActive ? 'Encerre a amostra antes de excluir — não é possível excluir enquanto está em andamento.' : ''}
                        placement="left"
                    >
                        <span>
                            <MenuItem
                                onClick={() => handleMenuAction(() => openModal('confirm'))}
                                dense
                                disabled={isActive}
                                sx={{ color: 'error.main' }}
                            >
                                <ListItemIcon><Lucide.Trash2 size={18} color={theme.palette.error.main} /></ListItemIcon>
                                Excluir
                            </MenuItem>
                        </span>
                    </Tooltip>
                </Permitido>
            </Menu>

            {modalType === 'history' && (
                <Dialog open onClose={closeModal} {...getModalPaperProps('sm')}>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Lucide.History color={theme.palette.action.active} /> Histórico
                    </DialogTitle>
                    <DialogContent dividers>
                        {sortedComments.map((c, i) => (
                            <Box key={i} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Stack direction="row" spacing={1} mb={0.5}>
                                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>{c.author?.[0]}</Avatar>
                                    <Typography variant="subtitle2">
                                        {c.author || 'Usuário'}{' '}
                                        <Typography component="span" variant="caption" color="text.secondary">
                                            • {dayjs(c.createdAt).format('DD/MM HH:mm')}
                                        </Typography>
                                    </Typography>
                                </Stack>
                                <Typography variant="body2">{c.text}</Typography>
                            </Box>
                        ))}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => { closeModal(); openModal('comment'); }}>Novo Comentário</Button>
                        <Button onClick={closeModal} variant="contained" disableElevation>Fechar</Button>
                    </DialogActions>
                </Dialog>
            )}

            <ResultadoLabDialog
                open={modalType === 'labResult'}
                onClose={closeModal}
                experimentId={experiment.id}
                entradas={entradasLab}
                labResult={labResult}
                onSalvo={(salvo) => {
                    setLabResult(salvo);
                    closeModal();
                    onAction?.({ type: 'refresh' });
                    setSnackbar({ open: true, message: 'Resultado salvo!', severity: 'success' });
                }}
            />

            {modalType === 'sample' && (
                <Dialog open onClose={closeModal} {...getModalPaperProps('sm')}>
                    <DialogTitle>Dados da Amostra</DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField autoFocus fullWidth label="Nome da Amostra" value={sampleInputs.nome}
                                onChange={(e) => setSampleInputs((prev) => ({ ...prev, nome: e.target.value }))} />
                            <TextField fullWidth label="Código da Amostra" value={sampleInputs.codigoAmostra}
                                onChange={(e) => setSampleInputs((prev) => ({ ...prev, codigoAmostra: e.target.value }))} />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeModal}>Cancelar</Button>
                        <Button onClick={saveSampleData} variant="contained" disableElevation>Salvar</Button>
                    </DialogActions>
                </Dialog>
            )}

            {modalType === 'trainingLabel' && pode('amostra.rotuloTreino') && (
                <Dialog open onClose={closeModal} {...getModalPaperProps('sm')}>
                    <DialogTitle>Label de Treinamento</DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField select label="Label" value={trainingLabelInput} onChange={(e) => setTrainingLabelInput(e.target.value)} fullWidth>
                                {Object.entries(TRAINING_LABEL_META).map(([value, meta]) => (
                                    <MenuItem key={value} value={value}>{meta.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField label="Motivo (opcional)" value={trainingLabelReason} onChange={(e) => setTrainingLabelReason(e.target.value)} fullWidth multiline rows={2} placeholder="Ex: dado fora de padrão para treino" />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeModal} disabled={savingTrainingLabel}>Cancelar</Button>
                        <Button onClick={saveTrainingLabel} variant="contained" disableElevation disabled={savingTrainingLabel}>
                            {savingTrainingLabel ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {modalType === 'comment' && (
                <Dialog open onClose={closeModal} {...getModalPaperProps('sm')}>
                    <DialogTitle>Adicionar Comentário</DialogTitle>
                    <DialogContent dividers>
                        <TextField autoFocus fullWidth label="Digite aqui" value={inputValue} onChange={(e) => setInputValue(e.target.value)} sx={{ mt: 1 }} multiline rows={3} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeModal}>Cancelar</Button>
                        <Button onClick={saveComment} disabled={!inputValue.trim()} variant="contained" disableElevation>Salvar</Button>
                    </DialogActions>
                </Dialog>
            )}

            <Dialog open={confirmEncerrarOpen} onClose={() => setConfirmEncerrarOpen(false)} {...getModalPaperProps('xs')}>
                <DialogTitle>Encerrar amostra</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2">Tem certeza que deseja encerrar esta amostra agora?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmEncerrarOpen(false)}>Não</Button>
                    <Button color="warning" variant="contained" disableElevation onClick={() => { setConfirmEncerrarOpen(false); setEncerrarOpen(true); }}>Sim</Button>
                </DialogActions>
            </Dialog>

            {encerrarOpen && (
                <Dialog open onClose={() => setEncerrarOpen(false)} {...getModalPaperProps('sm')}>
                    <DialogTitle>Confirmar Encerramento</DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Alert severity="warning">Esta ação irá parar a coleta de dados.</Alert>
                            <FormControlLabel
                                control={<Checkbox checked={encerrarForcado} onChange={(e) => setEncerrarForcado(e.target.checked)} />}
                                label="Forçar encerramento (ignorar validações)"
                            />
                            <TextField label="Motivo do encerramento" value={encerrarMotivo} onChange={(e) => setEncerrarMotivo(e.target.value)} fullWidth multiline rows={2} />
                            <FormControlLabel
                                control={<Checkbox checked={encerrarRemoveTipo} onChange={(e) => setEncerrarRemoveTipo(e.target.checked)} />}
                                label="Remover tipo de ensaio"
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEncerrarOpen(false)}>Voltar</Button>
                        <Button onClick={handleEncerrarConfirm} variant="contained" disableElevation color="warning" disabled={ending}>
                            {ending ? '...' : 'Confirmar'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {adminForcarOpen && (
                <Dialog open onClose={() => setAdminForcarOpen(false)} {...getModalPaperProps('sm')}>
                    <DialogTitle sx={{ color: 'error.main' }}>Forçar Encerramento (Admin)</DialogTitle>
                    <DialogContent dividers>
                        {ultimaLeituraInfo?.possivelmenteRodando ? (
                            <Alert severity="error" sx={{ mb: 2, fontWeight: 600 }}>
                                ⚠️ A torre mandou uma leitura há {formatSecondsAgo(ultimaLeituraInfo.secondsAgo)} —
                                ela pode ainda estar rodando fisicamente! Confirme visualmente antes de continuar.
                            </Alert>
                        ) : (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Isso marca o experimento como concluído diretamente no banco, sem esperar confirmação da torre.
                                Use somente quando a torre já parou fisicamente mas continua "ativa" aqui no CRM.
                            </Alert>
                        )}
                        <TextField
                            label="Motivo" value={adminForcarMotivo}
                            onChange={(e) => setAdminForcarMotivo(e.target.value)}
                            fullWidth multiline rows={2} placeholder="Obrigatório..."
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAdminForcarOpen(false)}>Cancelar</Button>
                        <Button
                            color="error" onClick={handleAdminForcarConfirm} variant="contained" disableElevation
                            disabled={forcandoAdmin || !adminForcarMotivo.trim()}
                        >
                            {forcandoAdmin ? '...' : 'Forçar Encerramento'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {modalType === 'confirm' && (
                <Dialog open onClose={closeModal} {...getModalPaperProps('xs')}>
                    <DialogTitle sx={{ color: 'error.main' }}>Excluir Amostra</DialogTitle>
                    <DialogContent dividers>
                        <Typography sx={{ mb: 2 }}>Esta é uma exclusão lógica. O dado não será apagado do banco, mas ficará oculto.</Typography>
                        {experiment.amostraId && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Este experimento faz parte da análise{experiment.codigoAmostra ? ` ${experiment.codigoAmostra}` : ''}.
                                Excluir tira o resultado dele de lá até ser restaurado.
                            </Alert>
                        )}
                        <TextField label="Motivo da exclusão" value={deleteMotivo} onChange={(e) => setDeleteMotivo(e.target.value)} fullWidth multiline rows={2} placeholder="Obrigatório..." />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeModal}>Cancelar</Button>
                        <Button color="error" onClick={handleDeleteConfirm} variant="contained" disableElevation disabled={deleting || !deleteMotivo.trim()}>
                            {deleting ? '...' : 'Excluir'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

export default React.memo(ExperimentCard);
